/**
 * SearchTab — the magnifier page, now with sub-tabs.
 * "Search" is the existing date-range labour/materials report. The other
 * five (Expenses, Hours, Invoices, Payroll, CRA) are placeholders for a
 * separate master set of databases we'll design one at a time — each will
 * get its own fields, display layout, and single/batch file import.
 */
import { useSearchParams } from "react-router-dom";
import LabourReportPanel from "./LabourReport";
import VendorDB from "./masterdb/VendorDB";
import InvoicesDB from "./masterdb/InvoicesDB";
import HoursDB from "./masterdb/HoursDB";
import PayrollDB from "./masterdb/PayrollDB";
import OverheadDB from "./masterdb/OverheadDB";

const MASTER_ADMIN_ROLES = ["owner", "superadmin", "admin"];

const SUBTABS = [
  { key: "search", label: "Search" },
  { key: "crossroads", label: "Crossroads" },
  { key: "hilti", label: "Hilti" },
  { key: "overhead", label: "Overhead" },
  { key: "hours", label: "Hours" },
  { key: "invoices", label: "Invoices" },
  { key: "payroll", label: "Payroll" },
  { key: "cra", label: "CRA" },
];

function PlaceholderDB({ label }) {
  return (
    <div
      className="mt-4 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-6 py-16 text-center"
      data-testid={`search-subtab-panel-${label.toLowerCase()}`}
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">{label}</p>
      <p className="mt-2 text-sm text-white/45">
        Blank for now — we'll design this database together (fields, layout, then file & batch import).
      </p>
    </div>
  );
}

export default function SearchTab({ me }) {
  // The sub-tab lives in the URL (?sub=) so refreshing the page or using
  // Back/Forward keeps you on the same sub-tab instead of resetting.
  const [searchParams, setSearchParams] = useSearchParams();
  // master databases are admin-only (server enforces too)
  const isMasterAdmin = MASTER_ADMIN_ROLES.includes(me?.role) && me?.permissions?.database !== false;
  const tabs = isMasterAdmin ? SUBTABS : SUBTABS.slice(0, 1);
  const subParam = searchParams.get("sub");
  const sub = tabs.some((t) => t.key === subParam) ? subParam : "search";
  const setSub = (key) =>
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (key === "search") p.delete("sub");
      else p.set("sub", key);
      return p;
    }, { replace: false });
  return (
    <div className="mx-auto max-w-5xl px-4 pb-16" data-testid="search-tab">
      <div className="mb-4 pt-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Job cost search</p>
        <h1 className="text-3xl font-extrabold uppercase tracking-tight text-white">Search</h1>
        <p className="mt-1 text-sm text-white/50">
          Pick a date range — every job with labour or materials in that period, plus the overhead that touched no job at all.
        </p>
      </div>

      {/* sub-tabs: existing search + the future master databases (admin-only) */}
      {isMasterAdmin && (
        <div className="mb-4 flex gap-1.5 overflow-x-auto rounded-lg border border-white/10 bg-white/[0.03] p-1.5" data-testid="search-subtabs">
          {tabs.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setSub(key)}
            data-testid={`search-subtab-${key}`}
            className={`flex-1 whitespace-nowrap rounded-md px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors ${
              sub === key
                ? "bg-[#1a3fd1] text-white shadow"
                : "border border-white/10 bg-white/[0.02] text-white/70 hover:border-white/25 hover:text-white"
            }`}
          >
            {label}
          </button>
          ))}
        </div>
      )}

      {sub === "search" || !isMasterAdmin ? (
        <LabourReportPanel showOverhead />
      ) : sub === "crossroads" ? (
        <VendorDB vendor="crossroads" title="Crossroads" />
      ) : sub === "hilti" ? (
        <VendorDB vendor="hilti" title="Hilti" />
      ) : sub === "invoices" ? (
        <InvoicesDB />
      ) : sub === "hours" ? (
        <HoursDB />
      ) : sub === "payroll" ? (
        <PayrollDB />
      ) : sub === "overhead" ? (
        <OverheadDB />
      ) : (
        <PlaceholderDB label={SUBTABS.find((s) => s.key === sub).label} />
      )}
    </div>
  );
}
